/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/types";
import { useMutation } from "@apollo/client";

import {
  CREATE_ANNOUNCEMENT,
  EDIT_ANNOUNCEMENT,
  DELETE_ANNOUNCEMENT,
} from "graphql/internal/Mutations";
import { skaleRed, skaleGreen } from "config/UpduoColors";
import { announcementModeList } from "config/Utilities";

import * as UpduoAnalytics from "config/UpduoAnalytics";
import {
  CreateAnnouncementMutation,
  DeleteAnnouncementMutation,
  EditAnnouncementMutation,
} from "graphql/internal/generated";

export default function useAnnouncementDetail({
  announcement,
  onRefresh,
  onDiscardNew,
  isNew,
}) {
  const authToken = useSelector((state: RootState) => state.authToken);

  const [isLoading, setIsLoading] = useState(false);
  const [alertColor, setAlertColor] = useState(skaleGreen);
  const [alertString, setAlertString] = useState("");
  const [isDeleteConfirmationShown, setIsDeleteConfirmationShown] =
    useState(false);

  const [clockedTime, setClockedTime] = useState([
    announcement.clockedTime
      ? new Date(Date.parse(announcement.clockedTime))
      : new Date(),
  ]);
  const [clockedTimeZone, setClockedTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [clockedTimeZoneOffset, setClockedTimeZoneOffset] = useState(
    new Date().getTimezoneOffset()
  );

  const [heading, setHeading] = useState(announcement.heading);
  const [content, setContent] = useState(announcement.content);

  const [mode, setMode] = useState<any>(
    announcementModeList.find((item) => item.id === announcement.mode)
  );

  const [modeMenuEl, setModeMenuEl] = useState(null);

  const [createAnnouncement, {}] = useMutation<CreateAnnouncementMutation>(
    CREATE_ANNOUNCEMENT,
    {
      onCompleted: ({ createAnnouncement }) => {
        if (!createAnnouncement?.ok) {
          console.error("ERROR createAnnouncement", createAnnouncement.reason);
          UpduoAnalytics.logEvent(
            "app-useannouncementdetail-createannouncement-error",
            {
              error: createAnnouncement.reason,
            }
          );
        } else {
          setIsLoading(false);
          onRefresh();
        }
      },
      onError: (error) => {
        console.error("ERROR createAnnouncement", error);
        UpduoAnalytics.logEvent(
          "app-useannouncementdetail-createannouncement-error",
          {
            error,
          }
        );
      },
    }
  );

  const [editAnnouncement, {}] = useMutation<EditAnnouncementMutation>(
    EDIT_ANNOUNCEMENT,
    {
      onCompleted: ({ editAnnouncement }) => {
        if (!editAnnouncement?.ok) {
          console.error("ERROR editAnnouncement", editAnnouncement?.reason);
          UpduoAnalytics.logEvent(
            "app-useannouncementdetail-editannouncement-error",
            {
              error: editAnnouncement?.reason,
            }
          );
        } else {
          setIsLoading(false);
          setAlertColor("green");
          setAlertString("Your changes are saved!");
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "app-useannouncementdetail-editannouncement-error",
          {
            error,
          }
        );
      },
    }
  );

  const [deleteAnnouncement, {}] = useMutation<DeleteAnnouncementMutation>(
    DELETE_ANNOUNCEMENT,
    {
      onCompleted: ({ deleteAnnouncement }) => {
        if (!deleteAnnouncement?.ok) {
          console.error("ERROR deleteAnnouncement", deleteAnnouncement?.reason);
          UpduoAnalytics.logEvent(
            "app-useannouncementdetail-deleteannouncement-error",
            {
              error: deleteAnnouncement?.reason,
            }
          );
        }
        onRefresh();
      },
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "app-useannouncementdetail-deleteannouncement-error",
          {
            error,
          }
        );
        onRefresh();
      },
    }
  );

  const _handleShowUnsavedWarning = () => {
    setAlertColor(skaleRed);
    setAlertString("You have unsaved changes.");
  };

  const _handleSave = useCallback(() => {
    setIsLoading(true);
    const canEdit = Boolean(authToken);
    if (!canEdit) {
      setAlertColor(skaleRed);
      setAlertString("Notification editing is currently disabled.");
      return;
    }
    const newClockedTimeZoneOffset =
      new Date().getTimezoneOffset() - clockedTimeZoneOffset;
    const newTimestamp = new Date(
      clockedTime[0].getTime() - newClockedTimeZoneOffset * 60 * 1000
    );
    const now = new Date();
    if (now.getTime() > newTimestamp.getTime()) {
      setIsLoading(false);
      setAlertColor(skaleRed);
      setAlertString("Delivery date must be scheduled in the future.");
      return;
    }
    const newAnnouncement = {
      timestamp: newTimestamp.toISOString(),
      heading,
      content,
      mode: mode.id,
    };
    if (isNew) {
      createAnnouncement({
        variables: {
          createAnnouncementMutationRequest: newAnnouncement,
        },
      });
    } else {
      editAnnouncement({
        variables: {
          editAnnouncementMutationRequest: Object.assign(
            { id: announcement.id },
            newAnnouncement
          ),
        },
      });
    }
    UpduoAnalytics.logEvent("user-announcement-detail-save", newAnnouncement);
  }, [
    authToken,
    clockedTime,
    clockedTimeZoneOffset,
    heading,
    content,
    mode,
    isNew,
    announcement.id,
  ]);

  const _handlePressDelete = useCallback(() => {
    UpduoAnalytics.logEvent("user-announcement-delete-confirm", announcement);
    deleteAnnouncement({
      variables: {
        deleteAnnouncementMutationRequest: {
          id: announcement.id,
        },
      },
    });
    setIsDeleteConfirmationShown(false);
  }, [announcement]);

  const _handleOnDeleteCancel = useCallback(() => {
    UpduoAnalytics.logEvent("user-announcement-delete-cancel", announcement);
    setIsDeleteConfirmationShown(false);
  }, [announcement]);

  const _handleDatePickerChange = useCallback(
    ({ date }) => {
      const newDate = Array.isArray(date) ? date : [date];
      newDate[0].setHours(clockedTime[0].getHours());
      newDate[0].setMinutes(clockedTime[0].getMinutes());
      setClockedTime(newDate);
      _handleShowUnsavedWarning();
    },
    [clockedTime]
  );

  const _handleTimePickerChange = (date) => {
    setClockedTime([date]);
    _handleShowUnsavedWarning();
  };

  const _handleTimezonePickerChange = useCallback(
    (timezone) => {
      if (timezone.offset || timezone.offset === 0) {
        const previousOffset = clockedTimeZoneOffset;
        setClockedTimeZone(timezone.id);
        setClockedTimeZoneOffset(timezone.offset);
        setClockedTime((previousState) => [
          new Date(
            previousState[0].getTime() +
              (previousOffset - timezone.offset) * 60 * 1000
          ),
        ]);
        _handleShowUnsavedWarning();
      }
    },
    [clockedTimeZoneOffset]
  );

  const _handleHeadingChange = (text) => {
    setHeading(text);
    _handleShowUnsavedWarning();
  };

  const _handleContentChange = (text) => {
    setContent(text);
    _handleShowUnsavedWarning();
  };

  const _handlePressDiscard = useCallback(() => {
    if (isNew) {
      onDiscardNew();
    } else {
      setIsDeleteConfirmationShown(!isDeleteConfirmationShown);
    }
  }, [isDeleteConfirmationShown, isNew]);

  const _handleClickModeMenu = (event) => {
    setModeMenuEl(event.currentTarget);
  };

  const _handleModeMenuClose = useCallback(() => {
    setModeMenuEl(null);
  }, []);

  const _handleChangeMode = useCallback(
    (value) => {
      if (mode.id !== value.id) {
        setMode(value);
        _handleShowUnsavedWarning();
      }
      _handleModeMenuClose();
    },
    [mode]
  );

  return {
    alertColor,
    alertString,
    clockedTime,
    clockedTimeZone,
    content,
    heading,
    isDeleteConfirmationShown,
    isLoading,
    mode,
    modeMenuEl,
    _handleContentChange,
    _handleDatePickerChange,
    _handlePressDiscard,
    _handleHeadingChange,
    _handleOnDeleteCancel,
    _handlePressDelete,
    _handleSave,
    _handleTimePickerChange,
    _handleTimezonePickerChange,
    _handleClickModeMenu,
    _handleModeMenuClose,
    _handleChangeMode,
  };
}
